import type { JourneyType } from 'src/enums/journey-type';
import type { MembershipDuration } from 'src/enums/membership-duration';
import type { PACKAGE_KEY } from 'src/enums/package-key';
import type { AgeRangeResponse } from 'src/models/responses/age-range-response';

export type PackagesResponse = {
  packages: Package[];
  addOns: AddOn[];
};

export type Package = {
  packageKey: PACKAGE_KEY;
  prices: Partial<Record<MembershipDuration, MembershipTypePrices>>;
  packageInformationGroupedByType: Record<PackageInformationType, PackageInformation[]>;
  rateCardOrdering?: Partial<Record<JourneyType, number | undefined>>;
  fixedFutureMembershipStartDate?: string;
  primaryMemberAgeRange: AgeRangeResponse;
};

export type AddOn = {
  addOnKey: MembershipAddOnType;
  prices: Partial<Record<MembershipDuration, MembershipAddOnPrices>>;
  addOnInformation: AddOnInformation;
  pricesByParentPackageKey: Partial<
    Record<PACKAGE_KEY, Partial<Record<MembershipDuration, MembershipAddOnPrices>>>
  >;
};

export enum MembershipAddOnType {
  CHILD_OLDER = 'CHILD_OLDER',
  CHILD = 'CHILD',
  INFANT = 'INFANT',
  NANNY = 'NANNY',
  PARTNER = 'PARTNER',
}

export enum PackageInformationType {
  BENEFIT = 'BENEFIT',
  DRAWBACK = 'DRAWBACK',
  DESCRIPTION = 'DESCRIPTION',
}

export type MembershipTypePrices = {
  individual: number;
  couple: number;
  family: number;
  joiningFee: number;
  promotions: PackagePromotion[];
};

export type MembershipAddOnPrices = {
  price: number;
  joiningFee: number;
};

export type PackageInformationDisplayText = {
  text: string;
  subtext?: string;
};

export type PackageInformation = {
  packageInformationId: number;
  iconKey?: string;
  orderingPriority?: number;
  displayTextByLanguage: Record<string, PackageInformationDisplayText>;
  code?: PackageInformationCodeType;
};

export type AddOnInformation = {
  benefits: string[];
  drawbacks: string[];
};

export type PackagePromotion = {
  promotionId: number;
  endDate: string;
  inHiddenMenuInClub: boolean;
  promotionItems: PromotionItem[];
  textByLanguage: Record<string, PromotionTextByLanguage>;
  termsAndConditionsItemsByLanguage: Record<string, string[]>;
  promotionLastUpdated: string;
};

export type PromotionItem = {
  type: PromotionType;
  memberTypes: MemberType[];
  savingType: SavingType;
  savingAmount: number;
  lengthInMonths: number;
  monthOfFirstDirectDebit?: string;
};

type PromotionTextByLanguage = {
  rateCardBannerTitle: string;
  rateCardBannerDescription: string;
  shortDescription: string;
  promotionalTermsAndConditionsTitle: string;
  introBannerTitle: string;
  introBannerSubtitle: string;
  introBannerLineOne: string;
  introBannerLineTwo: string;
  introBannerLineThree: string;
  name?: string;
  position: string;
};

export enum MemberType {
  PRIMARY = 'PRIMARY',
  CHILD_OLDER = 'CHILD_OLDER',
  CHILD = 'CHILD',
  INFANT = 'INFANT',
  NANNY = 'NANNY',
  PARTNER = 'PARTNER',
}

export const hasRateCardShortDescription = (textByLanguage: PromotionTextByLanguage): boolean => {
  return !!textByLanguage && !!textByLanguage.shortDescription;
};

export const hasRateCardBannerFields = (textByLanguage: PromotionTextByLanguage): boolean => {
  return (
    !!textByLanguage &&
    !!textByLanguage.rateCardBannerDescription &&
    !!textByLanguage.rateCardBannerTitle
  );
};

export enum SavingType {
  PERCENTAGE = 'PERCENTAGE',
  PRICE_REDUCTION_IN_PENNIES = 'PRICE_REDUCTION_IN_PENNIES',
  FIXED_PRICE_IN_PENNIES = 'FIXED_PRICE_IN_PENNIES',
}

export enum PromotionType {
  JOINING_FEE = 'JOINING_FEE',
  PRO_RATA = 'PRO_RATA',
  MEMBERSHIP_FEE = 'MEMBERSHIP_FEE',
  JOIN_NOW_PAY_LATER = 'JOIN_NOW_PAY_LATER',
}

export enum PackageInformationCodeType {
  ACCESS_TO_CLUBS_IN_TIER = 'ACCESS_TO_CLUBS_IN_TIER',
  ACCESS_TO_CLUBS_IN_TIER_NO_POPUP = 'ACCESS_TO_CLUBS_IN_TIER_NO_POPUP',
  SPA_RETREAT = 'SPA_RETREAT',
  SPA_RETREAT_NO_POPUP = 'SPA_RETREAT_NO_POPUP',
  MULTIPLE_CLUB_ACCESS_AND_SPA_RETREATS = 'MULTIPLE_CLUB_ACCESS_AND_SPA_RETREATS',
  PRIORITY_BOOKING = 'PRIORITY_BOOKING',
  PERSONALISED_WELLNESS_TESTING = 'PERSONALISED_WELLNESS_TESTING',
  COMPLIMENTARY_DAILY_DRINK = 'COMPLIMENTARY_DAILY_DRINK',
  MONTHLY_VIP_SPA_GUEST_PASS = 'MONTHLY_VIP_SPA_GUEST_PASS',
}
